import moment from "moment";

/*
 * Mappers for the read-only "from the patient's file" panel on step 1 of the
 * pregnancy flow (KIN-2058). Both reduce a chart record to a one-line entry —
 * the panel is a prompt to check the chart, not a second copy of it, so nothing
 * here tries to reproduce the full record.
 */

const asArray = (data) => {
  if (Array.isArray(data)) return data;
  // Some patient endpoints wrap the collection, others return it bare.
  if (Array.isArray(data?.data)) return data.data;

  return [];
};

const DISPLAY_DATE_FORMAT = "DD-MM-YYYY";

const toValidMoment = (date) => {
  if (!date) return null;

  const parsed = moment(date);

  return parsed.isValid() ? parsed : null;
};

/*
 * Which of a history row's two dates it is shown and ordered by (KIN-2067).
 *
 * The chart holds both diseaseDate (when the condition occurred) and
 * recordedDate (when it was typed onto the chart), and they disagree whenever
 * an old condition is entered late. A past-history list is about when the
 * condition happened, so diseaseDate wins; recordedDate is only a fallback for
 * rows that never had one.
 *
 * The fallback is labelled because the two dates mean different things
 * clinically, and an unlabelled one would be read as an onset date. The common
 * case stays a bare date so the list still scans as a column of dates.
 */
const toMedicalHistoryDate = (item) => {
  const diseaseDate = toValidMoment(item.diseaseDate);

  if (diseaseDate) {
    return {
      sortValue: diseaseDate.valueOf(),
      text: diseaseDate.format(DISPLAY_DATE_FORMAT),
    };
  }

  const recordedDate = toValidMoment(item.recordedDate);

  if (recordedDate) {
    return {
      sortValue: recordedDate.valueOf(),
      text: `recorded ${recordedDate.format(DISPLAY_DATE_FORMAT)}`,
    };
  }

  return { sortValue: null, text: "" };
};

/*
 * Newest first by the date actually displayed, because the fallback means the
 * backend's disease_date ordering does not match what the GP reads. Undated
 * rows go last rather than being treated as the oldest — we do not know when
 * they happened, so they are not part of the sequence. Equal dates keep the
 * backend's relative order (Array#sort is stable).
 */
const byDisplayedDateNewestFirst = (first, second) => {
  if (first.sortValue === null) return second.sortValue === null ? 0 : 1;

  if (second.sortValue === null) return -1;

  return second.sortValue - first.sortValue;
};

export const toMedicalHistoryEntries = (medicalHistory) =>
  asArray(medicalHistory)
    .map((item, index) => {
      const { sortValue, text } = toMedicalHistoryDate(item);

      return {
        sortValue,
        entry: {
          id: item.id ?? `medical-history-${index}`,
          primary:
            [item.diseaseCode, item.medicalDetails]
              .filter(Boolean)
              .join(" ")
              .trim() || "Entry with no detail recorded",
          secondary: text,
        },
      };
    })
    .sort(byDisplayedDateNewestFirst)
    .map(({ entry }) => entry);

export const toCurrentMedicationEntries = (medicationHistory) =>
  asArray(medicationHistory?.prescribed).map((medication, index) => ({
    id: medication.id ?? `medication-${index}`,
    primary:
      medication.tradeName ||
      medication.genericName ||
      "Medication with no name recorded",
    secondary: [
      medication.strength,
      medication.dosage,
      medication.form,
      medication.frequency,
    ]
      .filter(Boolean)
      .join(" · "),
  }));

/*
 * What a collapsed section's heading says about itself. "None" is a deliberate
 * word rather than a hidden badge: with the section shut, a blank heading and
 * an empty one would be indistinguishable, and "nothing recorded" is exactly
 * the thing a GP must be able to read off this panel without opening it.
 */
export const toSectionBadge = ({ isLoading, entries }) => {
  if (isLoading) return { text: "Loading…", tone: "muted" };

  if (!entries.length) return { text: "None", tone: "muted" };

  return { text: `${entries.length}`, tone: "count" };
};
